import React, { useState } from "react";

interface CollapsibleSectionProps {
  title: string | React.ReactNode;
  children: React.ReactNode;
  isOpen?: boolean;
  onToggle?: (isOpen: boolean) => void;
  variant?: "default" | "minimal" | "elevated";
  initiallyOpen?: boolean;
}

const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  title,
  children,
  isOpen: controlledIsOpen,
  onToggle,
  variant = "default",
  initiallyOpen = false
}) => {
  const [internalIsOpen, setInternalIsOpen] = useState(initiallyOpen);

  const isOpen = controlledIsOpen !== undefined ? controlledIsOpen : internalIsOpen;

  const handleToggle = () => {
    const newIsOpen = !isOpen;
    if (controlledIsOpen === undefined) {
      setInternalIsOpen(newIsOpen);
    }
    onToggle?.(newIsOpen);
  };

  const getVariantClasses = () => {
    switch (variant) {
      case "minimal":
        return {
          container: "bg-white border-l-4 border-primary-500",
          header: "bg-secondary-50 hover:bg-secondary-100 border-b border-secondary-200",
          content: "bg-white"
        };
      case "elevated":
        return {
          container: "bg-white shadow-medium border border-secondary-200",
          header: "bg-gradient-to-r from-primary-50 to-primary-100 hover:from-primary-100 hover:to-primary-200",
          content: "bg-white"
        };
      default:
        return {
          container: "bg-white border border-secondary-200 shadow-soft",
          header: "bg-secondary-50 hover:bg-secondary-100",
          content: "bg-white"
        };
    }
  };

  const variantClasses = getVariantClasses();

  return (
    <div className={`rounded-lg overflow-hidden transition-all duration-200 ${variantClasses.container}`}>
      <button
        type="button"
        className={`w-full px-6 py-4 text-left flex justify-between items-center transition-all duration-200 focus:outline-none focus:ring-0 ${variantClasses.header}`}
        onClick={handleToggle}
        aria-expanded={isOpen}
      >
        <span className="text-secondary-900 font-semibold text-base flex items-center w-full">{title}</span>
        <span
          className={`text-secondary-500 transition-transform duration-300 ease-in-out ${isOpen ? "rotate-180" : ""}`}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M7 10L12 15L17 10"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </button>
      <div
        className={`overflow-hidden transition-all duration-300 ease-in-out ${variantClasses.content} ${
          isOpen ? "max-h-[10000px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className={isOpen ? "" : "invisible"}>{children}</div>
      </div>
    </div>
  );
};

export { CollapsibleSection };
